import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAlert } from "../components/Confirm";
import { formatDate } from "../lib/format";
import { ModuleSwitcher } from "../components/ModuleSwitcher";
import { EntraUserPicker } from "../components/EntraUserPicker";

type Vals = Record<string, string>;

const ACUITY_KEYS = [
  { key: "suggested_acuity_low",       label: "Low" },
  { key: "suggested_acuity_medium",    label: "Medium" },
  { key: "suggested_acuity_high",      label: "High" },
  { key: "suggested_acuity_very_high", label: "Very High" },
];

export function Rules() {
  const qc = useQueryClient();
  const [moduleKey, setModuleKey] = useState<string>("holiday");
  // Module-scoped business rules (thresholds, flags, acuity defaults).
  const list = useQuery({ queryKey: ["policy", moduleKey], queryFn: () => api.policyByModule(moduleKey) });
  // System-scoped infrastructure keys (routing emails, validity, sender, SP sync).
  // Lives in its own card so it never gets mixed up with module-specific rules.
  const systemList = useQuery({ queryKey: ["policy", "system"], queryFn: () => api.policyByModule("system") });
  const [values, setValues] = useState<Vals>({});
  const [saved, setSaved] = useState<Vals>({});
  const [systemValues, setSystemValues] = useState<Vals>({});
  const [systemSaved, setSystemSaved] = useState<Vals>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!list.data) return;
    const next: Vals = {};
    for (const row of list.data.policy) next[row.key] = row.value;
    setValues(next);
    setSaved(next);
  }, [list.data, moduleKey]);

  useEffect(() => {
    if (!systemList.data) return;
    const next: Vals = {};
    for (const row of systemList.data.policy) next[row.key] = row.value;
    setSystemValues(next);
    setSystemSaved(next);
  }, [systemList.data]);

  const dirtyKeys = useMemo(
    () => Object.keys(values).filter((k) => (values[k] ?? "") !== (saved[k] ?? "")),
    [values, saved],
  );

  function set(key: string, val: string) {
    setValues((s) => ({ ...s, [key]: val }));
  }
  function setBool(key: string, val: boolean) {
    set(key, val ? "1" : "0");
  }
  function getBool(key: string): boolean {
    return (values[key] ?? "0") === "1";
  }

  // Acuity JSON helpers
  function getAcuity(key: string): { day: number; night: number; sleeps: number } {
    try { return JSON.parse(values[key] ?? '{"day":0,"night":0,"sleeps":0}'); }
    catch { return { day: 0, night: 0, sleeps: 0 }; }
  }
  function setAcuity(key: string, patch: Partial<{ day: number; night: number; sleeps: number }>) {
    const current = getAcuity(key);
    set(key, JSON.stringify({ ...current, ...patch }));
  }

  const systemDirtyKeys = useMemo(
    () => Object.keys(systemValues).filter((k) => (systemValues[k] ?? "") !== (systemSaved[k] ?? "")),
    [systemValues, systemSaved],
  );

  function setSystem(key: string, val: string) {
    setSystemValues((s) => ({ ...s, [key]: val }));
  }

  async function saveAll() {
    if (!dirtyKeys.length && !systemDirtyKeys.length) return;
    setSaving(true);
    try {
      for (const k of dirtyKeys) {
        await api.policyByModuleUpdate(moduleKey, k, values[k]);
      }
      for (const k of systemDirtyKeys) {
        await api.policyByModuleUpdate("system", k, systemValues[k]);
      }
      setSaved(values);
      setSystemSaved(systemValues);
      await qc.invalidateQueries({ queryKey: ["policy"] });
      await qc.invalidateQueries({ queryKey: ["rates"] }); // suggestions are pulled from rate-card endpoint
    } finally {
      setSaving(false);
    }
  }
  function discard() {
    setValues(saved);
    setSystemValues(systemSaved);
  }

  if (list.isLoading) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-liaise-navy">Rules</h1>
          <p className="text-slate-500 mt-1">Every threshold, flag and suggestion shown on the claimant tool is editable here. Each save is audited.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {(dirtyKeys.length + systemDirtyKeys.length) > 0 && (
            <span className="text-xs text-amber-700 bg-liaise-yellow/20 px-2 py-1 rounded-full whitespace-nowrap">
              {dirtyKeys.length + systemDirtyKeys.length} unsaved change{(dirtyKeys.length + systemDirtyKeys.length) === 1 ? "" : "s"}
            </span>
          )}
          <button onClick={discard} disabled={!dirtyKeys.length && !systemDirtyKeys.length}
            className="px-3 py-2 rounded-lg text-sm bg-white border border-slate-200 disabled:opacity-40 whitespace-nowrap">
            Discard
          </button>
          <button onClick={saveAll} disabled={(!dirtyKeys.length && !systemDirtyKeys.length) || saving}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-liaise-navy text-white disabled:opacity-40 whitespace-nowrap">
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      <ModuleSwitcher value={moduleKey} onChange={setModuleKey} filter="rules" />

      {moduleKey !== "holiday" && (
        <Card title="Module rules not defined yet" desc="">
          <div className="text-sm text-slate-500">
            The <b>{moduleKey}</b> module hasn't defined its own thresholds, flags or suggestion defaults yet.
            Once its data model is designed, drop the relevant cards into <code className="text-xs bg-slate-100 px-1 rounded">Rules.tsx</code> behind a <code className="text-xs bg-slate-100 px-1 rounded">moduleKey === "{moduleKey}"</code> guard.
            System-wide routing settings below apply to every module.
          </div>
        </Card>
      )}

      {/* Holiday-module-specific rule cards */}
      {moduleKey === "holiday" && (<>
      <Card title="Warning thresholds" desc="Surfaced as banners while the manager is building a holiday, hospital-stay or transition estimate. None of these block submission.">
        <div className="grid sm:grid-cols-3 gap-4">
          <NumField label="Above-threshold warning (£)" hint="Show a warning when the estimate cost exceeds this value."
            value={values.above_threshold_pounds ?? ""} onChange={(v) => set("above_threshold_pounds", v)} />
          <NumField label="Sanity green band (% of annual contribution)" hint="Cost below this % shows green."
            value={values.sanity_green_pct ?? ""} onChange={(v) => set("sanity_green_pct", v)} />
          <NumField label="Sanity amber band (% of annual contribution)" hint="Cost below this % shows amber. Above shows red."
            value={values.sanity_amber_pct ?? ""} onChange={(v) => set("sanity_amber_pct", v)} />
        </div>
      </Card>

      {/* Funder & PWS flags */}
      <Card title="Funder &amp; PWS flags" desc="Toggles that decide whether warning callouts show on the holiday, hospital-stay or transition form.">
        <div className="space-y-3">
          <Toggle label="Flag PWS with Partnership RAG = Red"
            desc="Surface a red callout on the new estimate form when the selected PWS is RAG-Red."
            on={getBool("flag_rag_red")} onChange={(v) => setBool("flag_rag_red", v)} />
          <Toggle label="Flag Case-by-Case funders"
            desc="Show an orange warning (plus SharePoint link + deadline) on the new estimate form when the funder is flagged as Case-by-Case. Separate from the Case-by-case pricing module above."
            on={getBool("flag_case_by_case")} onChange={(v) => setBool("flag_case_by_case", v)} />
        </div>
      </Card>

      {/* Acuity suggestions */}
      <Card title="Suggested incremental schedule by acuity"
            desc="When a manager picks a PWS, the form offers a one-click suggested schedule based on the PWS's acuity. These are the defaults applied per non-return day.">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left py-2 pr-3">Acuity</th>
              <th className="text-right px-3">Day hrs</th>
              <th className="text-right px-3">Night hrs</th>
              <th className="text-right px-3">Sleep-ins / night</th>
            </tr>
          </thead>
          <tbody>
            {ACUITY_KEYS.map(({ key, label }) => {
              const a = getAcuity(key);
              return (
                <tr key={key} className="border-t border-slate-100">
                  <td className="py-2 pr-3 font-medium text-liaise-navy">{label}</td>
                  <td className="px-3"><CellNumber value={a.day}    onChange={(v) => setAcuity(key, { day: v })} /></td>
                  <td className="px-3"><CellNumber value={a.night}  onChange={(v) => setAcuity(key, { night: v })} /></td>
                  <td className="px-3"><CellNumber value={a.sleeps} step={1} onChange={(v) => setAcuity(key, { sleeps: v })} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      </>)}

      {/* Routing — these are system-scoped, not module-scoped */}
      <Card title="Routing &amp; validity (system)" desc="System-wide settings: where finance-FYI emails go, what the From line says, and how long approved quotes are valid for. These apply across every module.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <TextField label="Finance CC email" hint="Recipient of the Finance-FYI email when an estimate is approved."
            placeholder="amir.X@liaise.com"
            value={systemValues.finance_cc_email ?? ""} onChange={(v) => setSystem("finance_cc_email", v)} />
          <TextField label="Sender mailbox"
            hint="The mailbox emails are sent from. The pricing-portal app must have Mail.Send permission on this mailbox."
            placeholder="commercial.finance@liaise.com"
            value={systemValues.notification_sender_email ?? ""} onChange={(v) => setSystem("notification_sender_email", v)} />
          <TextField label="Sender display name"
            hint="What recipients see as the From in their inbox. Use a generic name so it survives staff turnover."
            placeholder="Liaise Commercial Finance"
            value={systemValues.notification_sender_name ?? ""} onChange={(v) => setSystem("notification_sender_name", v)} />
          <NumField label="Quote validity (days)" hint="How many days the printable PDF says the quote is valid for."
            value={systemValues.quote_validity_days ?? ""} onChange={(v) => setSystem("quote_validity_days", v)} />
        </div>
      </Card>

      <ReferenceDataCard />
      <RegionRgmCard />
    </div>
  );
}

function RegionRgmCard() {
  const qc = useQueryClient();
  const alertModal = useAlert();
  const list = useQuery({ queryKey: ["regions"], queryFn: api.regions });
  const [addFor, setAddFor] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function add(region: string) {
    if (!email.trim() || !email.includes("@")) {
      await alertModal({ title: "Email required", body: "Enter a valid email for the RGM.", confirmLabel: "OK" });
      return;
    }
    setBusy(true);
    try {
      await api.rgmAdd({ region, rgm_email: email.trim().toLowerCase(), rgm_name: name.trim() || undefined });
      setAddFor(null); setEmail(""); setName("");
      await qc.invalidateQueries({ queryKey: ["regions"] });
    } catch (err) {
      await alertModal({ title: "Could not add RGM", body: (err as Error).message, confirmLabel: "OK" });
    } finally { setBusy(false); }
  }

  async function remove(id: number, label: string) {
    if (!confirm(`Remove ${label}? They will no longer be CC'd on emails for this region.`)) return;
    await api.rgmDelete(id);
    await qc.invalidateQueries({ queryKey: ["regions"] });
  }

  return (
    <Card title="Regional General Managers" desc="When an estimate is approved or sent to a funder, the RGM(s) for the home's region get CC'd. They are not pinged at submit or reject to avoid noise. Regions are pulled from the homes list.">
      {list.isLoading && <div className="text-sm text-slate-500">Loading...</div>}
      {list.data?.regions && (
        <div className="space-y-3">
          {list.data.regions.map((r: any) => (
            <div key={r.region} className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-liaise-navy">{r.region}</div>
                  <div className="text-xs text-slate-500">{r.home_count} home{r.home_count === 1 ? "" : "s"}</div>
                </div>
                {addFor !== r.region && (
                  <button onClick={() => { setAddFor(r.region); setEmail(""); setName(""); }}
                    className="text-xs px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50">
                    + Add RGM
                  </button>
                )}
              </div>
              {r.rgms.length > 0 && (
                <ul className="mt-3 divide-y divide-slate-100">
                  {r.rgms.map((g: any) => (
                    <li key={g.id} className="flex items-center justify-between py-2">
                      <div>
                        <div className="text-sm text-liaise-navy">{g.rgm_name || g.rgm_email}</div>
                        {g.rgm_name && <div className="text-xs text-slate-500">{g.rgm_email}</div>}
                      </div>
                      <button onClick={() => remove(g.id, g.rgm_name || g.rgm_email)}
                        className="text-xs text-slate-400 hover:text-liaise-red">Remove</button>
                    </li>
                  ))}
                </ul>
              )}
              {r.rgms.length === 0 && addFor !== r.region && (
                <div className="text-xs text-slate-400 italic mt-2">No RGM assigned.</div>
              )}
              {addFor === r.region && (
                <div className="mt-3 grid sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                  <div className="block">
                    <div className="text-xs font-medium text-slate-500 mb-1">Search Entra ID</div>
                    <EntraUserPicker
                      value={email}
                      onChange={(picked, raw) => {
                        if (picked) {
                          setEmail(picked.email);
                          if (!name.trim()) setName(picked.name);
                        } else {
                          setEmail(raw);
                        }
                      }}
                      placeholder="Type a name, e.g. Sarah Jones"
                      autoFocus
                    />
                  </div>
                  <label className="block">
                    <div className="text-xs font-medium text-slate-500 mb-1">Display name</div>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Auto-filled from Entra"
                      className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
                  </label>
                  <button onClick={() => add(r.region)} disabled={busy}
                    className="px-3 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-50">
                    {busy ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => setAddFor(null)}
                    className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm">Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ReferenceDataCard() {
  const qc = useQueryClient();
  const alertModal = useAlert();
  const status = useQuery({ queryKey: ["pws-sync-status"], queryFn: api.pwsSyncStatus });
  const [running, setRunning] = useState(false);

  async function runSync() {
    setRunning(true);
    try {
      const r = await api.pwsSyncRun();
      await alertModal({
        title: "Sync complete",
        body: `${r.read} item${r.read === 1 ? "" : "s"} read from SharePoint. ${r.updated} updated, ${r.skipped} unchanged${r.errors ? `, ${r.errors} error${r.errors === 1 ? "" : "s"}` : ""}.`,
        confirmLabel: "OK",
      });
      await qc.invalidateQueries({ queryKey: ["pws-sync-status"] });
    } catch (err) {
      await alertModal({ title: "Sync failed", body: (err as Error).message, confirmLabel: "OK" });
    } finally { setRunning(false); }
  }

  const s = status.data;
  const lastAt = s?.last_sync_at;
  const lastWhen = lastAt ? formatDate(lastAt) + " at " + new Date(lastAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "never";

  const openUrl = s?.list_url || s?.site_url;
  return (
    <Card title="Reference data" desc="PWS master data lives in a SharePoint list on the Group Finance Team site. James and Russell edit it there; this tool pulls it automatically every day and you can force a refresh below.">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex-1 min-w-[240px]">
          <div className="text-xs text-slate-500">Source</div>
          <div className="font-medium text-liaise-navy mt-0.5">
            {s?.list_name ?? "—"}
            {openUrl && <a href={openUrl} target="_blank" rel="noreferrer" className="text-xs text-liaise-blue hover:underline ml-2">Open list in SharePoint ↗</a>}
          </div>
          <div className="text-xs text-slate-500 mt-1">Auto-syncs daily at 06:00 UTC.</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Last synced</div>
          <div className="text-sm">{lastWhen}{s?.last_sync_count != null && <span className="text-slate-500"> · {s.last_sync_count} row{s.last_sync_count === 1 ? "" : "s"} updated</span>}</div>
        </div>
        <div className="ml-auto">
          <button onClick={runSync} disabled={running}
            className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-50"
            title="Force a manual override sync. Otherwise this runs automatically every day at 06:00 UTC.">
            {running ? "Syncing..." : "Sync now"}
          </button>
        </div>
      </div>
      {s?.last_sync_status && s.last_sync_status !== "ok" && s.last_sync_status !== "seeded" && (
        <div className="mt-3 text-xs text-red-700 bg-liaise-red/5 px-3 py-2 rounded">Last sync status: {s.last_sync_status}</div>
      )}
    </Card>
  );
}

/* ---------------- bits ---------------- */

function Card({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-liaise-navy" dangerouslySetInnerHTML={{ __html: title }} />
        <p className="text-xs text-slate-500 mt-1">{desc}</p>
      </div>
      {children}
    </section>
  );
}

function NumField({ label, hint, value, onChange }: { label: string; hint?: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      <input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}

function TextField({ label, hint, value, placeholder, onChange }: { label: string; hint?: string; value: string; placeholder?: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      <input type="text" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}

function Toggle({ label, desc, on, onChange }: { label: string; desc?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <div className="text-sm font-medium text-slate-800">{label}</div>
        {desc && <div className="text-xs text-slate-500 mt-1">{desc}</div>}
      </div>
      <button type="button" onClick={() => onChange(!on)}
        className={`shrink-0 inline-flex items-center w-11 h-6 rounded-full transition ${on ? "bg-liaise-green" : "bg-slate-200"}`}>
        <span className={`inline-block w-5 h-5 rounded-full bg-white shadow transform transition ${on ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

function CellNumber({ value, onChange, step = 0.25 }: { value: number; onChange: (n: number) => void; step?: number }) {
  return (
    <input type="number" step={step} min={0} value={value}
      onChange={(e) => onChange(Number(e.target.value || 0))}
      className="w-20 text-right text-sm border border-slate-200 rounded px-2 py-1 ml-auto block" />
  );
}
